import React from 'react';

const TECH_STACK = [
  'AWS · Azure · GCP',
  'Terraform · Pulumi · Ansible',
  'Kubernetes · Docker · Helm',
  'CI/CD · GitHub Actions · Jenkins',
  'Python · TypeScript · Go',
  'React · Node.js · PostgreSQL',
  'Claude AI · LLM APIs',
  'Grafana · Datadog · OpenTelemetry',
];

const CERTIFICATIONS = ['AWS', 'Azure', 'GCP', 'IBM', 'edX', 'Harvard'];

const About: React.FC = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left – certifications & tech */}
          <div className="space-y-6">
            {/* Certifications */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
                Certified On
              </p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {CERTIFICATIONS.map((cert) => (
                  <div
                    key={cert}
                    className="bg-white border border-gray-200 rounded-lg py-2.5 text-center text-sm font-semibold text-blue-700 shadow-sm"
                  >
                    {cert}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 w-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-semibold py-2.5 rounded-lg">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Certified Team
              </div>
            </div>

            {/* Tech stack */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
                Our Tech Stack
              </p>
              <div className="flex flex-wrap gap-2">
                {TECH_STACK.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right – company story */}
          <div className="space-y-6">
            <div>
              <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-3">
                About CloudIgnite
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                Built to Solve Real Business Problems
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  CloudIgnite was founded in 2026 to bring enterprise-grade 
                  software to small businesses across North America and Latin America. Our team combines 
                  deep engineering expertise with hands-on business consulting to deliver solutions that actually work.
                </p>
                <p>
                  Bilingual (EN/ES) and currently working with our first founding clients, we bridge the gap between 
                  complex technology and real business goals. Every solution we build is designed to be 
                  understood, owned, and grown by the people using it.
                </p>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {[
                { label: 'Results First', desc: 'We measure success by the impact on your business, not lines of code.' },
                { label: 'Built to Last', desc: 'Clean architecture and best practices that your team can maintain.' },
                { label: 'Full Ownership', desc: 'You own everything we build. No lock-in, no black boxes. Full source code delivery with every project. Host it anywhere, modify it anytime.' },
              ].map((v) => (
                <div key={v.label} className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-blue-700 font-semibold text-sm mb-1">{v.label}</p>
                  <p className="text-gray-600 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="border-l-4 border-blue-600 pl-5 py-1">
              <p className="text-gray-700 italic leading-relaxed">
                "The cloud is not a destination — it's a capability. Our goal is to make that capability
                accessible, reliable, and efficient for every business we work with."
              </p>
              <footer className="mt-2 text-sm font-semibold text-blue-700">— CloudIgnite</footer>
            </blockquote>

            {/* Action links */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href="#contact"
                className="inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
              >
                Start a Conversation
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center border border-gray-300 hover:border-blue-400 text-gray-700 hover:text-blue-700 font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
              >
                See Our Services
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;
